import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Camera, Upload, Users, Baby, User as UserIcon, Check, X } from 'lucide-react';
import { API_URL } from '../utils/api';

/**
 * MenuScan — dedicated flow for restaurant menu scans.
 *
 * 1. Pick profiles to include (multi-select). Defaults to all profiles.
 * 2. Snap or upload a menu photo.
 * 3. Backend extracts items + verdicts each item against each selected profile.
 * 4. Navigate to /menu-result/<id>.
 *
 * We do NOT use the existing CameraContext here — menu scanning is a one-shot flow
 * and doesn't need the persistent iOS stream. We use a plain file-input + getUserMedia
 * fallback via the same file input's `capture="environment"` hint.
 */
export default function MenuScan() {
  const [profiles, setProfiles] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [phase, setPhase] = useState('pick'); // pick | capture | processing
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);
  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API_URL}/profiles`, { withCredentials: true });
        const list = r.data.profiles || [];
        setProfiles(list);
        // Default: include ALL profiles for a whole-table scan
        setSelectedIds(new Set(list.map((p) => p.id)));
      } catch (e) {
        console.error('[MenuScan] Failed to load profiles:', e);
        toast.error('Could not load profiles');
      }
    };
    load();
  }, []);

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setPhase('capture');
  };

  const submit = async () => {
    if (selectedIds.size === 0) { toast.error('Pick at least one profile'); return; }
    if (!file) { toast.error('Add a menu photo first'); return; }
    setPhase('processing');
    if (navigator.vibrate) navigator.vibrate(40);
    try {
      const form = new FormData();
      form.append('file', file);
      const url = `${API_URL}/scan/menu?profile_ids=${encodeURIComponent(Array.from(selectedIds).join(','))}`;
      const r = await axios.post(url, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (navigator.vibrate) navigator.vibrate([40, 40, 40]);
      navigate(`/menu-result/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Menu scan failed');
      setPhase('capture');
    }
  };

  return (
    <div
      className="min-h-screen bg-qc-bg text-qc-text pb-28"
      style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top, 0px))' }}
      data-testid="menu-scan-screen"
    >
      {/* Header */}
      <div className="px-5 py-3 flex items-center max-w-lg mx-auto">
        <button
          onClick={() => navigate('/home')}
          data-testid="back-button"
          className="w-10 h-10 flex items-center justify-center rounded-full bg-qc-surface border border-qc-border text-qc-text tappable"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-2xl font-black text-qc-text ml-3 tracking-tight">Menu scan</h1>
      </div>

      <div className="max-w-lg mx-auto px-5 pt-3">
        {phase === 'pick' && (
          <div className="animate-fade-up" data-testid="menu-scan-pick-phase">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-2xl bg-qc-green/15 border border-qc-green/30 flex items-center justify-center flex-shrink-0">
                <Users className="w-6 h-6 text-qc-green" />
              </div>
              <div>
                <h2 className="text-xl font-black tracking-tight text-qc-text">Who&apos;s at the table?</h2>
                <p className="text-sm text-qc-text-dim mt-0.5">Pick everyone you want verdicts for.</p>
              </div>
            </div>

            <div className="space-y-2 mb-6" data-testid="menu-scan-profile-list">
              {profiles.map((p) => {
                const on = selectedIds.has(p.id);
                const Icon = p.is_kid ? Baby : UserIcon;
                return (
                  <button
                    key={p.id}
                    onClick={() => toggle(p.id)}
                    data-testid={`menu-scan-profile-${p.id}`}
                    className={`w-full flex items-center gap-3 p-3 rounded-2xl border-2 tappable text-left ${
                      on ? 'bg-qc-green/10 border-qc-green' : 'bg-qc-surface border-qc-border'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      on ? 'bg-qc-green text-qc-bg' : p.is_kid ? 'bg-qc-amber/15 text-qc-amber' : 'bg-qc-surface2 text-qc-text-dim'
                    }`}>
                      <Icon className="w-5 h-5" strokeWidth={2.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-base font-bold text-qc-text truncate">
                          {p.name || (p.is_primary ? 'You' : 'Profile')}
                        </span>
                        {p.is_kid && <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-qc-amber/15 text-qc-amber">Kid</span>}
                      </div>
                      <p className="text-xs text-qc-text-dim mt-0.5 capitalize truncate">
                        {p.medical_diet_type && p.medical_diet_type !== 'none'
                          ? p.medical_diet_type.replace('_', ' ')
                          : (p.eating_style && p.eating_style !== 'none' ? p.eating_style.replace('_', ' ') : (p.goal || 'general wellness').replace('_', ' '))}
                      </p>
                    </div>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${
                      on ? 'bg-qc-green border-qc-green' : 'border-qc-border'
                    }`}>
                      {on && <Check className="w-4 h-4 text-qc-bg" strokeWidth={3} />}
                    </div>
                  </button>
                );
              })}
            </div>

            <Button
              onClick={() => setPhase('capture')}
              disabled={selectedIds.size === 0}
              data-testid="menu-scan-continue-to-capture"
              className="w-full h-14 bg-qc-green hover:bg-qc-green-dark text-qc-bg rounded-2xl font-extrabold text-base shadow-[0_8px_24px_rgba(0,230,118,0.3)] disabled:opacity-30"
            >
              {selectedIds.size === 0 ? 'Pick at least one profile' : `Continue with ${selectedIds.size} profile${selectedIds.size === 1 ? '' : 's'}`}
            </Button>
          </div>
        )}

        {phase === 'capture' && (
          <div className="animate-fade-up" data-testid="menu-scan-capture-phase">
            <p className="text-sm text-qc-text-dim mb-3">
              Snap or upload a menu. We&apos;ll extract the dishes and call GO / MAYBE / HELL NO for each person.
            </p>

            {preview ? (
              <div className="relative rounded-2xl overflow-hidden border border-qc-border bg-qc-surface mb-4">
                <img src={preview} alt="Menu preview" className="w-full max-h-80 object-contain" />
                <button
                  onClick={() => { setPreview(null); setFile(null); }}
                  data-testid="menu-scan-remove-image"
                  className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/60 backdrop-blur text-white flex items-center justify-center"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                data-testid="menu-scan-image-dropzone"
                className="w-full aspect-[4/5] rounded-3xl border-2 border-dashed border-qc-border bg-qc-surface flex flex-col items-center justify-center gap-3 mb-4 tappable cursor-pointer"
              >
                <div className="w-16 h-16 rounded-full bg-qc-surface2 flex items-center justify-center text-qc-green">
                  <Camera className="w-8 h-8" />
                </div>
                <p className="text-base font-bold text-qc-text">Tap to take or upload</p>
                <p className="text-sm text-qc-text-dim">A photo of the menu</p>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFile}
              className="hidden"
              data-testid="menu-scan-file-input"
            />

            <div className="flex gap-2 mb-3">
              <Button
                onClick={() => setPhase('pick')}
                variant="outline"
                className="flex-1 h-12 bg-qc-surface border-qc-border text-qc-text rounded-xl font-bold"
                data-testid="menu-scan-back"
              >
                Back
              </Button>
              {preview ? (
                <Button
                  onClick={submit}
                  className="flex-1 h-12 bg-qc-green text-qc-bg rounded-xl font-extrabold"
                  data-testid="menu-scan-submit"
                >
                  Get verdicts
                </Button>
              ) : (
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex-1 h-12 bg-qc-green text-qc-bg rounded-xl font-extrabold"
                  data-testid="menu-scan-take-photo"
                >
                  <Upload className="w-4 h-4 mr-2" /> Add photo
                </Button>
              )}
            </div>
          </div>
        )}

        {phase === 'processing' && (
          <div className="flex-1 flex flex-col items-center justify-center py-16 animate-fade-up" data-testid="menu-scan-processing">
            <div className="relative mb-6">
              <div className="absolute inset-0 rounded-full bg-qc-green/20 blur-2xl animate-green-pulse" />
              <div className="relative w-24 h-24 rounded-full bg-qc-green flex items-center justify-center shadow-[0_0_60px_rgba(0,230,118,0.55)]">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-qc-bg border-t-transparent" />
              </div>
            </div>
            <p className="text-lg font-black text-qc-text mb-1">Reading the menu…</p>
            <p className="text-sm text-qc-text-dim">Calling verdicts for {selectedIds.size} {selectedIds.size === 1 ? 'person' : 'people'}.</p>
          </div>
        )}
      </div>
    </div>
  );
}
